import * as React from 'react'

import { Card } from '../../../../app/features/common/Card'
import { FooterWithButtons } from '../../components/FooterWithButtons'
import { DriverCell } from '../../components/DriverCell'
import { Driver, SelectedDriver } from '@/app/types/creator'
import { useParams } from 'react-router-dom'
import ActivityModal from './ActivityModal'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  selectedDrivers: SelectedDriver[]
  drivers: Driver[]
  year: number
  onNext: () => void
  onPrev: () => void
}

export const ActivityStep = ({
  selectedDrivers,
  drivers,
  year,
  onNext,
  onPrev,
}: Props) => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t, tSafe } = useLocalize()
  const columns = React.useMemo<TableHeaderColumn[]>(
    () => [
      {
        title: tSafe('creator.activity.column.driver', 'Kierowca'),
        key: 'name',
        type: 'string',
        sort: true,
      },
      {
        title: tSafe(
          'creator.activity.column.nonWorkingDays',
          'Ilość nieobecności',
        ),
        key: 'nonWorkingDays',
        type: 'number',
        sort: true,
      },
      {
        title: ' ',
        key: 'action',
        type: 'string',
        sort: false,
      },
    ],
    [tSafe],
  )
  const driverById = React.useMemo(() => {
    const map = new Map<string, Driver>()
    drivers.forEach((d) => map.set(d.id, d))
    return map
  }, [drivers])
  const [selectedDriverId, setSelectedDriverId] = React.useState<string | null>(
    null,
  )

  const rows = React.useMemo(
    () =>
      selectedDrivers.map((driver) => {
        const driverData = driverById.get(driver.driverId)
        return {
          driverId: driver.driverId,
          name: [driver.name, driver.surname].filter(Boolean).join(' '),
          nonWorkingDays: driverData?.nonWorkingDays ?? 0,
          _driver: driver,
        }
      }),
    [selectedDrivers, driverById],
  )

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: rows,
    tableColumns: columns,
    includeActiveSorting: false,
  })

  return (
    <Card
      className="tw-inline-block tw-h-full tw-max-w-full tw-bg-base-100 tw-pb-3 tw-pt-0"
      classNameContent="!tw-px-0 !tw-pt-0 tw-flex tw-flex-col tw-h-full"
      paddingTop={false}
    >
      <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-t-xl">
        {sortedData.length > 0 ? (
          <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto">
            <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
              <TableHeader
                opacity
                sticky
                rounded
                disabledCheckboxToggle
                columns={columns}
                fontSize="sm"
                sortConfig={sortConfig}
                onHeaderClick={handleHeaderClick}
              />
              <tbody className="tw-text-sm tw-text-content-base">
                {sortedData.map((row) => (
                  <tr
                    key={String(row.driverId)}
                    className="notches-border--right tw-relative tw-border-b tw-bg-white"
                  >
                    <TableTextRow size="medium" textSize="sm" pointer={false}>
                      <DriverCell name={String(row.name ?? '')} />
                    </TableTextRow>
                    <TableTextRow size="medium" textSize="sm" pointer={false}>
                      {String(row.nonWorkingDays ?? 0)}
                    </TableTextRow>
                    <TableTextRow size="medium" textSize="sm" pointer={false}>
                      <div className="tw-flex tw-w-full tw-items-center tw-justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedDriverId(String(row.driverId))
                          }
                          className="tw-text-primary tw-underline hover:tw-opacity-70"
                        >
                          {tSafe(
                            'creator.activity.enter',
                            'Wprowadź nieobecności',
                          )}
                        </button>
                      </div>
                    </TableTextRow>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <TableHeader
              opacity
              disabledCheckboxToggle
              columns={columns}
              sortConfig={null}
            />
            <TableEmpty title={t('driverModule.no-drivers')} />
          </>
        )}
      </section>
      <FooterWithButtons
        onNext={onNext}
        onPrev={onPrev}
        className="tw-shrink-0 tw-border-t tw-border-base-300"
      />
      {selectedDriverId !== null && (
        <ActivityModal
          driverId={selectedDriverId}
          driverName={
            rows.find((r) => r.driverId === selectedDriverId)?.name
          }
          companyId={companyId}
          year={year}
          close={() => setSelectedDriverId(null)}
        />
      )}
    </Card>
  )
}
